import { UUID } from "@bitory-gmbh/nidavellir";
import { documentDbPut } from "@/shared/lib/storage/indexdb/document-db";
import { GSMDocumentKind } from "@/services/backend/documents/types/kind";
import t from "@/lang/lang";
import {
  Query,
  useDocumentDBGetAll,
} from "@/shared/lib/storage/indexdb/document-db-hooks";
import {
  useHtzAhuComponentCatalogShowActiveCatalogQuery,
  useHtzAhuComponentCatalogShowQuery,
  useLazyHtzAhuComponentCatalogShowActiveCatalogQuery,
  useLazyHtzAhuComponentCatalogShowQuery,
} from "@/services/backend/htz/ahu/api/ahu-component-catalog-api";
import { AhuComponentCatalog } from "@/services/backend/htz/ahu/types/ahu-component-catalog";
import { useStaleContext } from "./stale-context";
import { OfflineState, useOfflineContext } from "./offline-context";

export function useAhuComponentCatalogOffline(
  workOrderId: UUID,
  companyId: string,
) {
  const { setSynced } = useOfflineContext();
  const { addStale } = useStaleContext();
  const [loadActive] = useLazyHtzAhuComponentCatalogShowActiveCatalogQuery();
  const [loadCatalog] = useLazyHtzAhuComponentCatalogShowQuery();

  const prepMsg = t("Speichere Bauteilkatalog ...");
  const doneMsg = t("Bauteilkatalog");

  const prepare = async () => {
    const activeResult = await loadActive({ companyId });
    if (activeResult.error) throw activeResult.error;
    if (!activeResult.data?.catalogId)
      throw new Error("No active catalog found");

    const catalogResult = await loadCatalog({
      id: activeResult.data.catalogId,
    });
    if (catalogResult.error) throw catalogResult.error;

    addStale("ahuComponentCatalog");
    setSynced((prev) => [...prev, prepMsg]);
    await documentDbPut(
      GSMDocumentKind.WorkOrder,
      workOrderId,
      "ahuComponentCatalog",
      catalogResult.data!,
    );
    setSynced((prev) => prev.map((p) => (p === prepMsg ? doneMsg : p)));
  };

  return { prepare };
}

export function useGetAhuComponentCatalog(
  workOrderId: string,
  companyId: string,
): Query<AhuComponentCatalog> {
  const { offlineState } = useOfflineContext();

  const {
    data: activeAhuComponentCatalog,
    isLoading: activeAhuComponentCatalogIsLoading,
    error: activeAhuComponentCatalogError,
  } = useHtzAhuComponentCatalogShowActiveCatalogQuery(
    { companyId },
    { skip: !companyId },
  );

  const {
    data: ahuComponentCatalog,
    isLoading: ahuComponentCatalogIsLoading,
    error: ahuComponentCatalogError,
  } = useHtzAhuComponentCatalogShowQuery(
    {
      id: activeAhuComponentCatalog?.catalogId ?? "",
    },
    {
      skip:
        activeAhuComponentCatalog === undefined ||
        activeAhuComponentCatalog?.catalogId === null,
    },
  );

  const {
    data: indexedDBData,
    isLoading: indexedDBIsLoading,
    error: indexedDBError,
  } = useDocumentDBGetAll<AhuComponentCatalog>(
    GSMDocumentKind.WorkOrder,
    workOrderId,
    "ahuComponentCatalog",
  );

  switch (offlineState) {
    case OfflineState.ONLINE:
      return {
        data: ahuComponentCatalog,
        isLoading:
          activeAhuComponentCatalogIsLoading || ahuComponentCatalogIsLoading,
        error: activeAhuComponentCatalogError || ahuComponentCatalogError,
      };
    case OfflineState.OFFLINE:
      return {
        data: indexedDBData ? indexedDBData[0] : undefined,
        isLoading: indexedDBIsLoading,
        error: indexedDBError,
      };
    default:
      return {
        data: undefined,
        isLoading: false,
        error: { status: 500 },
      };
  }
}
